import { useState } from 'react'
import { toast } from 'sonner'

import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { ChatActionWithValue } from '../../models/chat'
import { type LossRunSummary, updateLossRuns, uploadLossRuns } from '../../models/user'
import { ChatLossRunForm } from './ChatLossRunForm'
import { TypingDots } from './TypingIndicator'

interface EditLossRunsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
  defaultValues: LossRunSummary['defaultValues'] | undefined
  isParsing: boolean | undefined
  orgId: string
}

export function EditLossRunsDialog({
  open,
  onOpenChange,
  onSuccess,
  defaultValues,
  isParsing,
  orgId,
}: EditLossRunsDialogProps) {
  const [saving, setSaving] = useState(false)

  async function handleSubmit(data: ChatActionWithValue | FormData) {
    if (!(data instanceof FormData) && data.value === 'SKIP_LOSS_RUNS') {
      onOpenChange(false)
      return
    }

    setSaving(true)
    try {
      if (data instanceof FormData) {
        await uploadLossRuns(orgId, data.getAll('file') as File[])
        toast.success('Loss run reports uploaded — updating your application. This may take a few minutes.')
      } else {
        await updateLossRuns(orgId, data.value as LossRunSummary['defaultValues'])
        toast.success('Loss run history updated')
      }
      onSuccess()
      onOpenChange(false)
    } catch (err: any) {
      const message = err?.response?.data?.error ?? 'Failed to update loss run history'
      toast.error(message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" className="overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit loss run history</DialogTitle>
        </DialogHeader>
        {saving ? (
          <TypingDots />
        ) : (
          <ChatLossRunForm
            handleSubmit={handleSubmit}
            defaultValues={defaultValues as any}
            isParsing={isParsing}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}
